import { useEffect, useRef, useState } from "react";
import { Button, Col, DatePicker, Flex, Form, Input, InputNumber, Row, Switch, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMessageApi } from "../../context/MessageContext";
import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { DATE_FORMAT, DATE_FORMAT_API } from "../../constants/common";
import dayjs from "dayjs";
import { IEspTable, IEspTableData, IEspTableDataResponse } from "../../interfaces/expect-supplier-payment";
import { ExpectingSupplierPaymentServices } from "../../services/expect-supplier-payment";
import { renderClassNameStatusEsp, roundCost } from "../../utils/helper";
import FormattedNumberInput from "../../components/format-number-input";
import { EspStatusEnum } from "../../constants/enum";

const defaultRow: IEspTable = {
  status: "Unmatched",
  dueDate: undefined,
  localCurrencyCost: undefined,
  isActive: true,
};

const formatDateData = (dataForm: IEspTable[]) => {
  if (dataForm) {
    const converted = dataForm.map((item: IEspTable) => ({
      ...item,
      dueDate: item.dueDate ? dayjs(item.dueDate) : null,
    }));
    return converted;
  }
  return [];
};

interface CreateAndEditEspProps {
  onClose: (okTriggered?: boolean) => void;
  bookingRef: string;
  supplierCode: string;
  espId?: string;
  isPermissionEdit?: boolean;
}

const CreateAndEditEsp: React.FC<CreateAndEditEspProps> = ({
  onClose,
  bookingRef,
  supplierCode,
  espId,
  isPermissionEdit,
}) => {
  const messageApi = useMessageApi();
  const [form] = Form.useForm();
  const espTables = Form.useWatch("espTables", form);
  const [loading, setLoading] = useState<boolean>(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [addDisabled, setAddDisabled] = useState(false);

  const tableRef = useRef<HTMLDivElement>(null);
  const isNewRowAddedRef = useRef(false);

  useEffect(() => {
    form.resetFields();
    const fetchData = async () => {
      const params = {
        bookingRef: bookingRef,
        supplierCode: supplierCode,
      };
      try {
        setLoading(true);
        const response: IEspTableDataResponse = await ExpectingSupplierPaymentServices.getDetailBySupplierAndBooking({
          params: params,
        });
        const { success, data } = response;
        if (success) {
          form.setFieldsValue({
            ...data,
            espTables: [...formatDateData(data.espTables), ...(!espId ? [defaultRow] : [])],
          });
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bookingRef, supplierCode, form, espId]);

  useEffect(() => {
    const check = async () => {
      try {
        await form.validateFields();
        setAddDisabled(false);
      } catch {
        setAddDisabled(true);
      }
    };
    check();
  }, [espTables, form]);

  useEffect(() => {
    if (isNewRowAddedRef.current && tableRef.current) {
      const body = tableRef.current.querySelector(".ant-table-body");
      if (body) {
        body.scrollTop = body.scrollHeight;
      }
      isNewRowAddedRef.current = false;
    }
  }, [espTables]);

  const onFinish = async (values: IEspTableData) => {
    const textMsg = espId ? "Update" : "Create";
    try {
      const totalEspCost = roundCost(
        (values.espTables || [])
          .filter((r: IEspTable) => r.isActive)
          .reduce((sum: number, item: any) => sum + (item.localCurrencyCost || 0), 0)
      );
      const bscCost = roundCost(values?.bsc?.localCurrencyCost || 0);

      if (totalEspCost !== bscCost) {
        messageApi.error(
          `Cost mismatched between ESP and BSC, the remaining cost is ${(bscCost - totalEspCost).toFixed(2)}`
        );
        return;
      }

      setIsLoadingSubmit(true);
      const body = values.espTables.map((esp: IEspTable) => ({
        ...esp,
        dueDate: dayjs(esp.dueDate).format(DATE_FORMAT_API),
      }));
      await ExpectingSupplierPaymentServices.updateDetailBookingSupplierCost(bookingRef, supplierCode, body);
      messageApi.success(`${textMsg} Expected Supplier Payment success!`);
      onClose(true);
    } catch (err: any) {
      const message = err.message ?? "Expected Supplier Payment failed!";
      messageApi.error(`${textMsg} ${message}`);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const isCheckDisabledRow = (row: IEspTable) => {
    const { isActive, status } = row || {};
    if (!isActive || status === EspStatusEnum.EXPORTED) {
      return true;
    }
    return false;
  };

  return (
    <Form form={form} layout="horizontal" onFinish={onFinish} disabled={!isPermissionEdit}>
      <Row gutter={24}>
        <Col span={12}>
          <Form.Item label="Supplier Code" name={["bsc", "supplierCode"]} labelCol={{ span: 11 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Supplier Name" name={["bsc", "supplierName"]} labelCol={{ span: 11 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Booking Reference ID" name={["bsc", "bookingRef"]} labelCol={{ span: 11 }}>
            <Input readOnly />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="Local Currency Cost (BSC)" name={["bsc", "localCurrencyCost"]} labelCol={{ span: 11 }}>
            <FormattedNumberInput className="w-full " readOnly />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="GBP Cost (BSC)" name={["bsc", "gbpCost"]} labelCol={{ span: 11 }}>
            <FormattedNumberInput className="w-full " readOnly  disabled={true}/>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="GBP Cost (ESP)" name={["bsc", "espGbpCost"]} labelCol={{ span: 11 }}>
            <FormattedNumberInput className="w-full " readOnly  disabled={true}/>
          </Form.Item>
        </Col>
      </Row>
      {!loading && (
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const bscCost = roundCost(getFieldValue(["bsc", "localCurrencyCost"]));
            const espTables = getFieldValue(["espTables"]) || [];
            const totalEspCost = roundCost(
              espTables
                .filter((r: IEspTable) => r.isActive)
                .reduce((sum: number, r: any) => sum + (r.localCurrencyCost || 0), 0)
            );

            if (totalEspCost !== bscCost) {
              return (
                <div
                  className="text-error"
                  style={{ textAlign: "center", marginBottom: 16 }}
                >{`Cost mismatched between ESP and BSC, the remaining cost is ${(bscCost - totalEspCost).toFixed(
                  2
                )}`}</div>
              );
            }
            return null;
          }}
        </Form.Item>
      )}

      <Form.List name="espTables">
        {(fields, { add }) => {
          const columns: ColumnsType<any> = [
            {
              title: "ESP No",
              dataIndex: "no",
              width: 80,
              render: (_: any, __: any, index: number) => index + 1,
            },
            {
              title: "Status",
              dataIndex: "status",
              width: 150,
              render: (_: any, record: any) => (
                <Form.Item shouldUpdate>
                  {({ getFieldValue }) => {
                    const status = getFieldValue(["espTables", record.name, "status"]);
                    return <span className={renderClassNameStatusEsp(status)}>{status}</span>;
                  }}
                </Form.Item>
              ),
            },
            {
              title: "Due Date",
              dataIndex: "dueDate",
              width: 260,
              render: (_: any, record: any) => (
                <Form.Item
                  name={[record.name, "dueDate"]}
                  rules={[{ required: espTables[record.name].isActive, message: "" }]}
                >
                  <DatePicker
                    className="w-full input-t-r"
                    format={DATE_FORMAT}
                    placeholder="Select due date"
                    disabled={!isPermissionEdit || isCheckDisabledRow(espTables?.[record.name])}
                  />
                </Form.Item>
              ),
            },
            {
              title: "Local Currency Cost (ESP)",
              dataIndex: "localCurrencyCost",
              width: 260,
              render: (_: any, record: any) => (
                <Form.Item
                  name={[record.name, "localCurrencyCost"]}
                  rules={[{ required: espTables[record.name].isActive, message: "" }]}
                >
                  <FormattedNumberInput
                    className="w-full input-t-r"
                    placeholder="Insert cost data here"
                    disabled={!isPermissionEdit || isCheckDisabledRow(espTables?.[record.name])}
                  />
                </Form.Item>
              ),
            },
            {
              title: "GBP (ESP)",
              dataIndex: "gbpCost",
              width: 260,
              render: (_: any, record: any) => (
                <Form.Item
                  name={[record.name, "gbpCost"]}
                >
                  <FormattedNumberInput
                    className="w-full input-t-r"
                    disabled={true}
                    readOnly
                  />
                </Form.Item>
              ),
            },
            {
              title: "Active",
              dataIndex: "isActive",
              width: 100,
              align: "center" as const,
              render: (_: any, record: any) => (
                <Form.Item name={[record.name, "isActive"]} valuePropName="checked" style={{ display: "inline-block" }}>
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                    disabled={!isPermissionEdit || espTables[record.name].status === "Exported"}
                  />
                </Form.Item>
              ),
            },
          ];

          return (
            <div ref={tableRef}>
              <Table
                dataSource={fields}
                pagination={false}
                rowKey={(f) => f.key}
                columns={columns}
                size="small"
                className="table-fm"
                scroll={{ y: 50 * 6 }}
                loading={loading}
              />

              {isPermissionEdit && (
                <Button
                  color="primary"
                  variant="outlined"
                  block
                  icon={<PlusOutlined />}
                  onClick={() => {
                    isNewRowAddedRef.current = true;
                    add({ ...defaultRow });
                  }}
                  style={{ marginTop: "16px" }}
                  disabled={addDisabled}
                >
                  Add row
                </Button>
              )}
            </div>
          );
        }}
      </Form.List>

      <Flex justify="end" gap={16} style={{ marginTop: 20 }}>
        <Button
          style={{ marginLeft: 8 }}
          onClick={() => onClose()}
          disabled={isLoadingSubmit}
          color="primary"
          variant="outlined"
        >
          Cancel
        </Button>
        {isPermissionEdit && (
          <Button type="primary" htmlType="submit" loading={isLoadingSubmit}>
            Save
          </Button>
        )}
      </Flex>
    </Form>
  );
};

export default CreateAndEditEsp;
